import React, { useState, forwardRef, useMemo } from 'react';
import { Button, Modal, Form, InputGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt } from 'react-icons/fa';

// Custom input with calendar icon
const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
  <InputGroup onClick={onClick} style={{ cursor: 'pointer', width: '200px' }}>
    <Form.Control
      type="text"
      value={value}
      readOnly
      ref={ref}
    />
    <InputGroup.Text>
      <FaCalendarAlt color="#004aad" />
    </InputGroup.Text>
  </InputGroup>
));

function FilterModel({ show, handleClose, onFilterApply, accountOptions = [], typeOptions = [] }) {
  // Default to last 30 days
  const defaultEndDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 30);

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [type, setType] = useState('All');
  const [accounts, setAccounts] = useState(['All']);

  // Sort accounts alphabetically with "All" always first
  const filteredAccountOptions = useMemo(() => {
    if (accountOptions.length === 0) return ['All'];
    // Create a copy of accountOptions
    const options = [...accountOptions];
    // Separate "All" if it exists
    const allIndex = options.indexOf('All');
    let allOption = null;
    if (allIndex !== -1) {
      allOption = options.splice(allIndex, 1)[0];
    }
    // Sort remaining options alphabetically (case-insensitive)
    options.sort((a, b) => {
      // Handle null/undefined cases
      if (!a) return -1;
      if (!b) return 1;
      // Case-insensitive comparison
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
    // Add "All" at the beginning if it existed
    if (allOption) {
      return [allOption, ...options];
    }
    // If "All" wasn't in original array, add it at beginning
    return ['All', ...options];
  }, [accountOptions]);

  // Ensure typeOptions has 'All'
  const filteredTypeOptions = typeOptions.length > 0 ? typeOptions : ['All', 'Debit', 'Payment'];

  const handleAccountChange = (e) => {
    const options = Array.from(e.target.selectedOptions, (option) => option.value);
    setAccounts(options);
  };

  const applyFilter = () => {
    // Prepare filter data
    const filterData = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      type: type !== 'All' ? type : null,
      accounts: accounts.length > 0 && !accounts.includes('All') ? accounts : null
    };
    // Pass filter data to parent component
    if (onFilterApply) {
      onFilterApply(filterData);
    }
    handleClose();
  };

  return (
    <>
      <style>
        {`
          .resizable-modal-dialog {
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: 95vw !important;
            max-height: 95vh !important;
          }
          .resizable-modal-content {
            resize: both !important; /* Resizable magic */
            overflow: auto !important; /* Scrollbar when content overflows */
            min-width: 420px; /* Minimum width to fit content */
            min-height: 520px;
            max-width: 95vw;
            max-height: 95vh;
            width: 530px; /* Starting width */
            height: 560px; /* Starting height */
            border: 1px solid #ccc;
            border-radius: 6px;
            background: white;
            margin-top: 155px;
          }
          /* Override Bootstrap defaults for overflow */
          .resizable-modal-content .modal-body {
            overflow: visible !important;
          }
          .resizable-modal-dialog .modal-dialog-scrollable .modal-content {
            overflow: visible !important;
          }
          /* Parameters container - fixed width to prevent stretching, stuck to left */
          .parameters-container {
            width: fit-content; /* Hugs the content width */
            min-width: 420px; /* Minimum to match modal min-width */
            max-width: 500px; /* Prevent excessive stretching */
            margin: 0; /* Stuck to left (no auto margin) */
            background-color: #f4f4f4;
            padding: 1rem;
            border-top: 2px solid #004aad;
            overflow-x: auto; /* Horizontal scrollbar if needed when shrinking */
          }
          /* Field row - flex for alignment */
          .field-row {
            display: flex;
            align-items: center;
            margin-bottom: 1rem;
          }
          /* Label fixed width */
          .field-label {
            width: 120px; /* Fixed label width */
            margin-right: 1rem;
            flex-shrink: 0; /* Don't shrink */
          }
          /* Input fixed widths */
          .type-select {
            width: 200px; /* Fixed width for type select */
          }
          .accounts-select {
            width: 250px; /* Fixed width for accounts select */
            height: 115px;
            padding: 0;
            border: 1px solid #808080;
            border-radius: 0;
            background-color: white;
            font-size: 14px;
            font-family: Arial, sans-serif;
          }
          .accounts-option {
            padding: 8px 12px;
            margin: 0;
          }
          .accounts-option:not(:last-child) {
            border-bottom: 1px dotted #aaa;
          }
          .accounts-option:selected {
            background-color: #aaa;
          }
        `}
      </style>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        dialogClassName="resizable-modal-dialog"
        contentClassName="resizable-modal-content"
      >
        <Modal.Header closeButton style={{ backgroundColor: '#004aad' }}>
          <Modal.Title style={{ color: '#fff' }}>Filter Postings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <h4 style={{ color: '#004aad', marginBottom: '20px' }}>Parameters</h4>
            <div className="parameters-container">
              <fieldset>
                {/* START DATE */}
                <div className="field-row">
                  <Form.Label className="field-label">
                    Start Date <span style={{ color: 'red' }}>*</span>
                  </Form.Label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="yyyy-MM-dd"
                    maxDate={endDate}
                    customInput={<CustomDateInput />}
                  />
                </div>
                {/* END DATE */}
                <div className="field-row">
                  <Form.Label className="field-label">
                    End Date <span style={{ color: 'red' }}>*</span>
                  </Form.Label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="yyyy-MM-dd"
                    minDate={startDate}
                    maxDate={new Date()}
                    customInput={<CustomDateInput />}
                  />
                </div>
                {/* TYPE */}
                <div className="field-row">
                  <Form.Label className="field-label">
                    Type <span style={{ color: 'red' }}>*</span>
                  </Form.Label>
                  <Form.Select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="type-select"
                  >
                    {filteredTypeOptions.map((typeOpt, index) => (
                      <option key={index} value={typeOpt}>
                        {typeOpt}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                {/* ACCOUNTS - ALPHABETICALLY SORTED */}
                <div className="field-row" style={{ alignItems: 'flex-start' }}>
                  <Form.Label className="field-label">Accounts</Form.Label>
                  <Form.Control
                    as="select"
                    multiple
                    value={accounts}
                    onChange={handleAccountChange}
                    className="accounts-select"
                    isInvalid={accounts.length === 0}
                  >
                    {filteredAccountOptions.map((acc, index) => (
                      <option
                        key={index}
                        value={acc}
                        className="accounts-option"
                        style={{
                          backgroundColor: accounts.includes(acc) ? '#aaa' : 'transparent',
                        }}
                      >
                        {acc}
                      </option>
                    ))}
                  </Form.Control>
                </div>
              </fieldset>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ backgroundColor: "#004aad", borderColor: "#004aad" }}
            onClick={applyFilter}
          >
            Filter
          </Button>
          <Button variant="light" onClick={handleClose}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default FilterModel;